import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

// ── Formatters ──────────────────────────────────────────────────────────────
function fmt(n)         { return Math.round(n).toLocaleString('en-US'); }
function fmtUSD(n)      { return '$' + fmt(n); }
function fmtShort(n)    {
  if (n >= 1e6)  return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
}

// ── Shared UI components ─────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--color-border-light)',
      borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem',
      borderLeft: `4px solid ${accent || 'var(--color-blue)'}`,
      boxShadow: 'var(--shadow-sm)',
    }}>
      <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem' }}>{label}</p>
      <p style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--color-dark-blue)', lineHeight: 1.1, marginBottom: '0.25rem' }}>{value}</p>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{sub}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem', marginTop: '1.75rem' }}>
      {children}
    </h3>
  );
}


// %% Sensitivity heatmap (Moebius Bleed only) %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
function BleedSensitivityChart({ assessment }) {
  const { results: r, inputs: inp } = assessment;

  const INV_DAYS  = [3, 5, 7, 9, 11, 13, 15];
  const AG_PRICES = [20, 30, 40, 50, 60, 70, 80, 90, 100];
  const TROY_OZ_PER_KG = 32.15074656;

  const daily_ag_kg   = Number(inp.bleed_volume_l_per_day) * Number(inp.bleed_ag_g_per_l) / 1000;
  const emew_rec_days = Number(inp.emew_recovery_days) || 1;
  const lease_rate    = Number(inp.silver_lease_rate_pct_per_year) || 0;

  // Isolate the financing saving so we can substitute different (days, price) combos
  const comp_fin = (r.competitor_by_category && r.competitor_by_category.financing) || 0;
  const emew_fin = (r.emew_by_category      && r.emew_by_category.financing)       || 0;
  const non_fin_saving = r.annual_saving - (comp_fin - emew_fin);

  // Build matrix [row=inv_days, col=price]
  const matrix = INV_DAYS.map(days =>
    AG_PRICES.map(price => {
      const fin = (days - emew_rec_days) * daily_ag_kg * TROY_OZ_PER_KG * price * lease_rate / 100;
      return Math.round(non_fin_saving + fin);
    })
  );

  const allVals = matrix.flat();
  const maxVal  = Math.max(...allVals);
  const minVal  = Math.min(...allVals);
  const range   = maxVal - minVal || 1;

  const currentDays  = Number(inp.total_inventory_days);
  const currentPrice = Number(inp.silver_price_usd_per_troy_oz);

  // Exact-match or bracket logic for row highlighting
  const exactDays = INV_DAYS.includes(currentDays);
  const lowerDays = exactDays ? null : ([...INV_DAYS].reverse().find(d => d <= currentDays) ?? null);
  const upperDays = exactDays ? null : (INV_DAYS.find(d => d > currentDays) ?? null);

  function cellBg(val) {
    const t = (val - minVal) / range; // 0–1
    if (val <= 0) {
      // red gradient
      const intensity = Math.min(1, Math.abs(val) / (Math.abs(minVal) || 1));
      return `rgb(255,${Math.round(220 - 120 * intensity)},${Math.round(220 - 120 * intensity)})`;
    }
    // yellow (#FFFBEA) → green (#1B6B3A)
    const rc = Math.round(255 - (255 - 27)  * t);
    const gc = Math.round(251 - (251 - 107) * t);
    const bc = Math.round(234 - (234 - 58)  * t);
    return `rgb(${rc},${gc},${bc})`;
  }

  function cellFg(val) {
    const t = (val - minVal) / range;
    return t > 0.55 ? '#fff' : '#1A2B3C';
  }

  return (
    <>
      <SectionTitle>Sensitivity Analysis — Annual Saving (Inventory Days vs Silver Price)</SectionTitle>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginBottom: '0.75rem' }}>
        Annual saving (USD/yr) for varying total inventory days and Ag price, all other inputs held constant.
        {' '}<strong>Bold border</strong> = current assessment inputs.
      </p>
      <div className="card" style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.8rem', width: '100%' }}>
          <thead>
            <tr style={{ background: '#EBF0FA' }}>
              <th style={{ padding: '0.55rem 0.9rem', textAlign: 'left', fontWeight: 700,
                           color: 'var(--color-dark-blue)', whiteSpace: 'nowrap',
                           borderRight: '2px solid #D1DAED' }}>
                Inv. Days ↓ &nbsp;/&nbsp; Ag Price →
              </th>
              {AG_PRICES.map(p => {
                const isCurCol = p === currentPrice;
                return (
                  <th key={p} style={{ padding: '0.55rem 0.4rem', textAlign: 'center', fontWeight: 700,
                                       color: 'var(--color-dark-blue)', whiteSpace: 'nowrap',
                                       borderLeft: isCurCol ? '2px solid var(--color-dark-blue)' : '1px solid #D1DAED',
                                       borderRight: isCurCol ? '2px solid var(--color-dark-blue)' : undefined,
                                       background: isCurCol ? '#D8E6FB' : undefined }}>
                    ${p}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {INV_DAYS.map((days, ri) => {
              const isCurRow  = days === currentDays;
              const isNearest = !exactDays && (days === lowerDays || days === upperDays);
              const isUpper   = isNearest && days === upperDays;

              const trBorderTop  = isCurRow  ? '2px solid var(--color-dark-blue)'
                                 : isUpper   ? '2px solid #6A9FD0'
                                 : '1px solid #E2E8F0';
              const labelBg      = isCurRow  ? '#D8E6FB'
                                 : isNearest ? '#EEF4FD'
                                 : '#F8FAFC';
              const labelBorderB = isCurRow  ? '2px solid var(--color-dark-blue)'
                                 : isNearest ? '2px solid #6A9FD0'
                                 : undefined;
              const labelText    = isNearest
                ? (days === lowerDays ? '▼ ' : '▲ ') + days + ' days'
                : days + ' days';

              return (
                <tr key={days} style={{ borderTop: trBorderTop }}>
                  <td style={{ padding: '0.4rem 0.9rem', fontWeight: 700, whiteSpace: 'nowrap',
                                color: 'var(--color-dark-blue)', background: labelBg,
                                borderRight: '2px solid #D1DAED',
                                borderBottom: labelBorderB }}>
                    {labelText}
                  </td>
                  {AG_PRICES.map((price, ci) => {
                    const val       = matrix[ri][ci];
                    const isCurCol  = price === currentPrice;
                    const isCurCell = isCurRow && isCurCol;
                    return (
                      <td key={price} style={{
                        padding: '0.4rem 0.4rem',
                        textAlign: 'center',
                        background: cellBg(val),
                        color: cellFg(val),
                        fontWeight: isCurCell ? 800 : 500,
                        whiteSpace: 'nowrap',
                        outline: isCurCell ? '2px solid var(--color-dark-blue)' : undefined,
                        outlineOffset: '-2px',
                        borderLeft:   isCurCol  ? '2px solid var(--color-dark-blue)' : '1px solid rgba(0,0,0,0.06)',
                        borderRight:  isCurCol  ? '2px solid var(--color-dark-blue)' : undefined,
                        borderBottom: isCurRow  ? '2px solid var(--color-dark-blue)'
                                    : isNearest ? '2px solid #6A9FD0' : undefined,
                        boxShadow:    isNearest && !isCurCell ? 'inset 0 0 0 1px #6A9FD080' : undefined,
                      }}>
                        {fmtShort(val)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── v2 result view ───────────────────────────────────────────────────────────
const CATEGORY_LABELS = {
  labour:     'Labour',
  energy:     'Energy',
  reagents:   'Reagents',
  maintenance:'Maintenance',
  financing:  'Financing',
  revenue:    'Add. Revenue',
};

const CATEGORIES = ['labour', 'energy', 'reagents', 'maintenance', 'financing', 'revenue'];

function V2Results({ assessment }) {
  const { results: r, inputs: inp } = assessment;
  const [showDetails, setShowDetails] = useState(false);

  // Build per-category saving (competitor cost – emew cost, taking revenue sign into account)
  const saving_by_cat = {};
  for (const cat of CATEGORIES) {
    const comp = r.competitor_by_category[cat] || 0;
    const emew = r.emew_by_category[cat]       || 0;
    // For revenue: competitor "revenue" = silver loss (cost); emew "revenue" = recovered value (reduces cost)
    // Net contribution to saving = comp[cat] + emew[cat] (emew revenue already included in emew_total)
    saving_by_cat[cat] = cat === 'revenue'
      ? comp + emew   // comp loss + emew recovery = total revenue advantage
      : comp - emew;  // lower OPEX saving
  }

  // Detailed line items by category for drill-down
  const itemsByCategory = {};
  for (const item of (r.line_items || [])) {
    const cat = item.category;
    if (!itemsByCategory[cat]) itemsByCategory[cat] = { competitor: [], emew: [] };
    itemsByCategory[cat][item.technology].push(item);
  }

  return (
    <>
      {/* ── KPI cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '0.5rem' }}>
        <KpiCard label="Annual Saving"    value={fmtShort(r.annual_saving)}      sub="emew vs Moebius ER (OPEX + revenue)" accent="var(--color-green)" />
        <KpiCard label="One-Time Saving"  value={fmtShort(r.one_time_saving.total)} sub={r.one_time_saving.avoided_capex > 0 ? `${fmtShort(r.one_time_saving.avoided_capex)} upcoming capex avoided` : 'No upcoming capex entered'} accent="var(--color-blue)" />
        <KpiCard label="Viable Total Investment" value={`Upto USD ${fmtShort(r.recommended_price)}`} sub={`${inp.customer_payback_years || 3}× annual saving + 1× one-time saving`} accent="#805AD5" />
      </div>

      {/* ── OPEX Comparison ── */}
      <SectionTitle>Annual OPEX Comparison — Moebius ER vs emew EW</SectionTitle>
      <div className="card" style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#EBF0FA' }}>
              <th style={{ padding: '0.6rem 1rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Cost Category</th>
              <th style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', whiteSpace: 'nowrap' }}>Moebius ER / yr</th>
              <th style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', whiteSpace: 'nowrap' }}>emew EW / yr</th>
              <th style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)', whiteSpace: 'nowrap' }}>Saving / yr</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((cat, i) => {
              const comp    = r.competitor_by_category[cat] || 0;
              const emewVal = r.emew_by_category[cat]       || 0;
              const saving  = saving_by_cat[cat];
              const isRev   = cat === 'revenue';
              return (
                <tr key={cat} style={{ borderTop: '1px solid var(--color-border)', background: i % 2 === 0 ? '#fff' : '#FAFBFD' }}>
                  <td style={{ padding: '0.45rem 1rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {CATEGORY_LABELS[cat]}
                    {isRev && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 6 }}>(loss vs gain)</span>}
                  </td>
                  <td style={{ padding: '0.45rem 1rem', textAlign: 'right', color: comp > 0 ? '#C53030' : 'var(--color-text-muted)' }}>
                    {comp > 0 ? fmtUSD(comp) : '—'}
                    {isRev && comp > 0 && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 4 }}>loss</span>}
                  </td>
                  <td style={{ padding: '0.45rem 1rem', textAlign: 'right', color: emewVal > 0 ? (!isRev ? '#C53030' : 'var(--color-green)') : 'var(--color-text-muted)' }}>
                    {emewVal > 0 ? (isRev ? `+${fmtUSD(emewVal)}` : fmtUSD(emewVal)) : '—'}
                    {isRev && emewVal > 0 && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 4 }}>revenue</span>}
                  </td>
                  <td style={{ padding: '0.45rem 1rem', textAlign: 'right', fontWeight: 700, color: saving > 0 ? 'var(--color-green)' : saving < 0 ? '#C53030' : 'var(--color-text-muted)' }}>
                    {saving !== 0 ? (saving > 0 ? '+' : '') + fmtUSD(saving) : '—'}
                  </td>
                </tr>
              );
            })}
            {/* Total row */}
            <tr style={{ background: 'var(--color-dark-blue)', color: '#fff' }}>
              <td style={{ padding: '0.6rem 1rem', fontWeight: 700, color: '#fff' }}>Net Annual Cost / Saving</td>
              <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: '#fff' }}>{fmtUSD(r.competitor_total)}</td>
              <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: '#fff' }}>{fmtUSD(r.emew_total)}</td>
              <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-green)' }}>+{fmtUSD(r.annual_saving)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Line item drill-down ── */}
      <div style={{ marginBottom: '1rem' }}>
        <button
          onClick={() => setShowDetails(d => !d)}
          style={{ fontSize: '0.8125rem', color: 'var(--color-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          {showDetails ? '▲ Hide detail breakdown' : '▼ Show detail line items'}
        </button>
      </div>
      {showDetails && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: '#EBF0FA' }}>
                <th style={{ padding: '0.5rem 1rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Line Item</th>
                <th style={{ padding: '0.5rem 1rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Technology</th>
                <th style={{ padding: '0.5rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)' }}>USD / yr</th>
              </tr>
            </thead>
            <tbody>
              {(r.line_items || []).map((item, i) => (
                <tr key={item.key} style={{ borderTop: '1px solid var(--color-border)', background: i % 2 === 0 ? '#fff' : '#FAFBFD' }}>
                  <td style={{ padding: '0.375rem 1rem', color: 'var(--color-text-primary)' }}>
                    <span style={{ fontSize: '0.7rem', background: '#F0F1F3', borderRadius: 3, padding: '1px 5px', marginRight: 6, color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{item.category}</span>
                    {item.label}
                    {item.is_revenue && item.technology === 'emew' && <span style={{ fontSize: '0.7rem', color: 'var(--color-green)', marginLeft: 5 }}>▲ revenue</span>}
                  </td>
                  <td style={{ padding: '0.375rem 1rem', color: item.technology === 'emew' ? 'var(--color-green)' : '#C53030', fontWeight: 600 }}>
                    {item.technology === 'emew' ? 'emew EW' : 'Moebius ER'}
                  </td>
                  <td style={{ padding: '0.375rem 1rem', textAlign: 'right', fontWeight: 600, color: item.is_revenue && item.technology === 'emew' ? 'var(--color-green)' : 'var(--color-text-primary)' }}>
                    {item.is_revenue && item.technology === 'emew' ? '+' : ''}{fmtUSD(item.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── One-time savings ── */}
      <SectionTitle>One-Time Saving (on Switch to emew)</SectionTitle>
      <div className="card" style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: '#EBF0FA' }}>
              <th style={{ padding: '0.6rem 1rem', textAlign: 'left', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Item</th>
              <th style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-dark-blue)' }}>Value (USD)</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderTop: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.45rem 1rem', color: 'var(--color-text-primary)' }}>
                Upcoming capex avoided
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginLeft: 8 }}>
                  Planned spend on existing installation that becomes unnecessary
                </span>
              </td>
              <td style={{ padding: '0.45rem 1rem', textAlign: 'right', fontWeight: 600, color: 'var(--color-blue)' }}>
                {r.one_time_saving.avoided_capex > 0 ? fmtUSD(r.one_time_saving.avoided_capex) : '—'}
              </td>
            </tr>
            <tr style={{ background: 'var(--color-dark-blue)' }}>
              <td style={{ padding: '0.6rem 1rem', fontWeight: 700, color: '#fff' }}>Total one-time saving</td>
              <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: 'var(--color-green)' }}>{fmtUSD(r.one_time_saving.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>


  
            {/* Sensitivity heatmap — Moebius Bleed only */}
            {inp.app_type === 'moebius_bleed' && <BleedSensitivityChart assessment={assessment} />}
    </>
  );
}

// ── v1 legacy view (unchanged) ────────────────────────────────────────────────
function V1Results({ assessment }) {
  const { results: r, inputs } = assessment;
  const recoveryGain = (inputs.emew_recovery_pct - inputs.current_recovery_pct).toFixed(1);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '0.5rem' }}>
        <KpiCard label="Annual Value"   value={fmtShort(r.annual_value_usd)} sub="additional silver revenue" accent="var(--color-green)" />
        <KpiCard label="Recovery Gain"  value={`+${recoveryGain}%`} sub={`${inputs.current_recovery_pct}% → ${inputs.emew_recovery_pct}%`} accent="var(--color-blue)" />
        {r.payback_years
          ? <KpiCard label="Payback Period" value={`${r.payback_years} yrs`} sub="simple payback" accent="var(--color-blue)" />
          : <KpiCard label="Monthly Value"  value={fmtShort(r.monthly_value_usd)} sub="per month" accent="var(--color-blue)" />
        }
      </div>
      <SectionTitle>Value Analysis</SectionTitle>
      <div className="card" style={{ overflow: 'hidden', marginBottom: '0.5rem' }}>
        <table>
          <thead><tr><th>Metric</th><th style={{ textAlign: 'right' }}>Quantity</th><th style={{ textAlign: 'right' }}>Value (USD)</th></tr></thead>
          <tbody>
            {[
              ['Silver in solution (annual)',    `${fmt(r.silver_kg_per_year)} kg/yr`,      ''],
              ['Currently lost (unrecovered)',   `${fmt(r.current_loss_kg)} kg/yr`,          fmtUSD(r.current_loss_value_usd) + '/yr'],
              ['Currently recovered',            `${fmt(r.currently_recovered_kg)} kg/yr`,  `(${inputs.current_recovery_pct}%)`],
              ['With emew technology',           `${fmt(r.emew_recovered_kg)} kg/yr`,       `(${inputs.emew_recovery_pct}%)`],
              ['Additional silver recovered',    `${fmt(r.additional_silver_kg)} kg/yr`,    ''],
            ].map(([m, q, v], i) => (
              <tr key={m}><td>{m}</td><td style={{ textAlign: 'right' }}>{q}</td><td style={{ textAlign: 'right', color: v.startsWith('$') ? 'var(--color-blue)' : undefined }}>{v}</td></tr>
            ))}
            <tr style={{ background: 'var(--color-dark-blue)', color: '#fff' }}>
              <td style={{ fontWeight: 700, color: '#fff' }} colSpan={2}>Annual value opportunity</td>
              <td style={{ textAlign: 'right', fontWeight: 700, color: '#fff' }}>{fmtUSD(r.annual_value_usd)}/yr</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AssessmentResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/assessment/${id}`);
      setAssessment(res.data.data);
    } catch { navigate('/'); }
    finally  { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  async function downloadPDF() {
    setDownloading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`/api/assessment/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `emew_value_assessment_${assessment.customer_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to generate PDF'); }
    finally  { setDownloading(false); }
  }

  async function deleteAssessment() {
    if (!window.confirm(`Delete the assessment for ${assessment.customer_name}?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/assessment/${id}`);
      toast.success('Assessment deleted');
      navigate('/');
    } catch { setDeleting(false); }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
      <span className="spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
    </div>
  );

  if (!assessment) return null;

  const createdDate = new Date(assessment.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const isV2 = assessment.results?.engine === 'v2';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 0' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <Link to="/" style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: '0.5rem' }}>
            ← All assessments
          </Link>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{assessment.customer_name}</h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
            {[assessment.contact_person, assessment.country, assessment.application].filter(Boolean).join('  ·  ')}
            {' '}· {createdDate}
            {isV2 && <span style={{ marginLeft: 8, fontSize: '0.75rem', background: '#EBF0FA', color: 'var(--color-blue)', borderRadius: 4, padding: '1px 6px', fontWeight: 600 }}>{assessment.inputs?.app_type === 'moebius_bleed' ? 'Moebius Bleed' : 'Moebius Replacement'}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button className="btn btn-primary" onClick={downloadPDF} disabled={downloading}>
            {downloading ? <><span className="spinner" style={{ marginRight: 6 }} />Generating…</> : '⬇ Download PDF'}
          </button>
          <Link to="/assessment/new" className="btn btn-outline">New Assessment</Link>
          {user?.isAdmin && (
            <button className="btn btn-outline" onClick={deleteAssessment} disabled={deleting}
              style={{ color: '#E53E3E', borderColor: '#FED7D7' }}>
              Delete
            </button>
          )}
        </div>
      </div>

      {isV2 ? <V2Results assessment={assessment} /> : <V1Results assessment={assessment} />}

      {assessment.notes && (
        <>
          <SectionTitle>Notes</SectionTitle>
          <div className="card" style={{ padding: '1rem 1.25rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem', lineHeight: 1.6 }}>
            {assessment.notes}
          </div>
        </>
      )}
    </div>
  );
}
